/**
 * Animated favicon — paints video frames onto a 64×64 canvas and writes the
 * dataURL into <link rel="icon"> so the browser tab plays the brand video.
 *
 * - Prefers the transparent WebM (VP9 + alpha) so the tab icon has real
 *   transparency on its background. Falls back to the H.264 MP4 (opaque
 *   black square) when WebM/VP9 isn't supported.
 * - Throttled to ~10 fps (favicons don't need 60).
 * - Pauses when the tab is hidden (visibilitychange).
 * - Honors prefers-reduced-motion: leaves the static favicon in place.
 * - Same-origin video so the canvas isn't tainted; toDataURL works freely.
 */

const WEBM_URL = "/assets/logo/logo-animated.webm";
const MP4_URL = "/assets/logo/logo-animated.mp4";
const SIZE = 64;
const FPS = 10;
const FRAME_INTERVAL_MS = 1000 / FPS;

let started = false;

export const initAnimatedFavicon = () => {
    if (started) return;
    started = true;

    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const link = ensureIconLink();
    const canvas = document.createElement("canvas");
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext("2d", { willReadFrequently: false });
    if (!ctx) return;

    const video = document.createElement("video");
    // Pick the best source the browser can decode. WebM/VP9 carries the
    // alpha channel; MP4 is the opaque-bg fallback (e.g. older Safari).
    const webmSupported = video.canPlayType('video/webm; codecs="vp9"') !== "";
    video.src = webmSupported ? WEBM_URL : MP4_URL;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = "auto";
    video.crossOrigin = "anonymous";
    // Keep the element off the layout but still allowed to decode frames.
    video.style.position = "fixed";
    video.style.width = "1px";
    video.style.height = "1px";
    video.style.opacity = "0";
    video.style.pointerEvents = "none";
    video.style.left = "-9999px";

    let lastFrame = 0;
    let rafId = 0;

    const draw = (now: number) => {
        if (now - lastFrame >= FRAME_INTERVAL_MS && video.readyState >= 2) {
            ctx.clearRect(0, 0, SIZE, SIZE);
            ctx.drawImage(video, 0, 0, SIZE, SIZE);
            try {
                link.href = canvas.toDataURL("image/png");
            } catch {
                // Canvas tainted (shouldn't happen for same-origin video) — bail.
                cancelAnimationFrame(rafId);
                return;
            }
            lastFrame = now;
        }
        rafId = requestAnimationFrame(draw);
    };

    const start = () => {
        if (rafId) return;
        video.play().catch(() => {
            // Autoplay blocked — favicon stays static. No retry.
        });
        rafId = requestAnimationFrame(draw);
    };

    const stop = () => {
        if (!rafId) return;
        cancelAnimationFrame(rafId);
        rafId = 0;
        video.pause();
    };

    video.addEventListener("loadeddata", () => {
        document.body.appendChild(video);
        if (!document.hidden) start();
    });

    document.addEventListener("visibilitychange", () => {
        if (document.hidden) stop();
        else if (video.readyState >= 2) start();
    });
};

const ensureIconLink = (): HTMLLinkElement => {
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"][data-animated]');
    if (link) return link;
    link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }
    link.type = "image/png";
    link.dataset.animated = "true";
    return link;
};
